/* tslint:disable */
/* eslint-disable */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { Configuration } from '../configuration';
import {
    SDKError,
    AuthenticationError,
    ValidationError,
    NotFoundError,
    ConflictError,
    RateLimitError,
    ServerError,
    NetworkError
} from './errors';

/**
 * HTTP Client for making requests to the backend API
 * Handles authentication, error handling, and request/response interceptors
 */
export class HttpClient {
    private axios: AxiosInstance;
    private configuration: Configuration;

    constructor(configuration: Configuration) {
        this.configuration = configuration;

        // Create axios instance with base configuration
        this.axios = axios.create({
            baseURL: configuration.basePath || 'http://localhost:8000',
            timeout: configuration.timeout || 30000,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...configuration.headers
            },
            ...configuration.baseOptions
        });

        this.setupInterceptors();
    }

    /**
     * Setup request and response interceptors
     */
    private setupInterceptors(): void {
        // Request interceptor - add authentication headers
        this.axios.interceptors.request.use(
            async (config) => {
                // Add API Key if present
                if (this.configuration.apiKey) {
                    let apiKey: string | undefined;
                    if (typeof this.configuration.apiKey === 'string') {
                        apiKey = this.configuration.apiKey;
                    } else if (this.configuration.apiKey instanceof Promise) {
                        apiKey = await this.configuration.apiKey;
                    } else if (typeof this.configuration.apiKey === 'function') {
                        apiKey = await this.configuration.apiKey('x-api-key');
                    }
                    if (apiKey) {
                        config.headers['x-api-key'] = apiKey;
                    }
                }

                // Add user ID from frontend postMessage identity
                if (this.configuration.userId) {
                    config.headers['x-user-id'] = this.configuration.userId;
                }

                // Add Access Token (Cognito) if present
                if (this.configuration.accessToken) {
                    let token: string | undefined;
                    if (typeof this.configuration.accessToken === 'string') {
                        token = this.configuration.accessToken;
                    } else if (this.configuration.accessToken instanceof Promise) {
                        token = await this.configuration.accessToken;
                    } else if (typeof this.configuration.accessToken === 'function') {
                        token = await this.configuration.accessToken();
                    }
                    if (token) {
                        config.headers['Authorization'] = `Bearer ${token}`;
                    }
                }

                // Add Basic Auth if present (overwrites Bearer when both are set)
                if (this.configuration.username && this.configuration.password) {
                    const credentials = btoa(`${this.configuration.username}:${this.configuration.password}`);
                    config.headers['Authorization'] = `Basic ${credentials}`;
                }

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor - handle errors
        this.axios.interceptors.response.use(
            (response) => response,
            (error) => {
                return Promise.reject(this.handleError(error));
            }
        );
    }

    /**
     * Handle HTTP errors and convert to SDK error types
     */
    private handleError(error: AxiosError): Error {
        if (error.response) {
            // Server responded with error status
            const { status, data } = error.response;
            const message = (data as any)?.message || error.message || 'An error occurred';

            switch (status) {
                case 400:
                    return new ValidationError(message, status, data);
                case 401:
                case 403:
                    return new AuthenticationError(message, status, data);
                case 404:
                    return new NotFoundError(message, status, data);
                case 409:
                    return new ConflictError(message, status, data);
                case 429:
                    return new RateLimitError(message, status, data);
                case 500:
                case 502:
                case 503:
                case 504:
                    return new ServerError(message, status, data);
                default:
                    return new SDKError(message, status, data);
            }
        } else if (error.request) {
            // Request was made but no response received
            return new NetworkError('No response from server', error.request);
        } else {
            // Something happened in setting up the request
            return new SDKError(error.message || 'Request setup failed');
        }
    }

    /**
     * Perform GET request
     */
    public async get<T>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axios.get<T>(url, {
            params,
            ...config
        });
        return response.data;
    }

    /**
     * Perform POST request
     */
    public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axios.post<T>(url, data, config);
        return response.data;
    }

    /**
     * Perform PUT request
     */
    public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axios.put<T>(url, data, config);
        return response.data;
    }

    /**
     * Perform DELETE request
     */
    public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axios.delete<T>(url, config);
        return response.data;
    }

    /**
     * Perform PATCH request
     */
    public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axios.patch<T>(url, data, config);
        return response.data;
    }

    /**
     * Get the underlying axios instance (for advanced usage)
     */
    public getAxiosInstance(): AxiosInstance {
        return this.axios;
    }
}
